import { USD } from "@dinero.js/currencies";
import { isAxiosError } from "axios";
import { dinero } from "dinero.js";
import _ from "lodash";
import { z } from "zod";
import { BaseConnector } from "../../baseConnector";
import { DigikeyAuthenticator } from "./authenticator";
export class DigikeyConnector extends BaseConnector {
    rateLimit = 5000;
    rateLimitRemaining = 5000;
    authenticator;
    constructor({ credentialManager }) {
        super();
        this.authenticator = new DigikeyAuthenticator({ credentialManager });
    }
    get baseUrl() {
        return "https://api.digikey.com";
    }
    async search(params, isRetry = false) {
        let keyword;
        if ("mpn" in params) {
            keyword = params.mpn;
        }
        else if ("keyword" in params) {
            keyword = params.keyword;
        }
        try {
            const response = await this.post("/Search/v3/Products/Keyword", {
                Keywords: keyword,
                RecordCount: 10,
                RequestedQuantity: 1,
            }, {
                params: {
                    includes: "SearchLocaleUsed,ExactManufacturerProductsCount,ExactManufacturerProducts",
                },
            });
            this.updateRateLimit(response.headers);
            const results = processSearchPayload(ExactSearchResponseOrErrorResponseSchema.parse(response.payload));
            return results;
        }
        catch (err) {
            if (isAxiosError(err) && err.response?.status === 401 && !isRetry) {
                // NOTE: for unknown reasons, the Digi-Key API sometimes returns
                // a 401, despite the token being valid, and it is fixed by
                // refreshing the token.
                void (await this.authenticator.refreshAccessToken());
                return this.search(params, true);
            }
            throw err;
        }
    }
    // From Digi-Key API FAQ: Keyword search data is cached and may be up to 24
    // hours stale. Please use ProductDetails for real time pricing and availability.
    async getProductDetails(digikeyPartNumber) {
        const response = await this.get(`/Search/v3/Products/${encodeURIComponent(digikeyPartNumber)}`);
        this.updateRateLimit(response.headers);
        return DigikeyProductDetailsSchema.parse(response.payload);
    }
    async getManufacturerList() {
        const response = await this.get("/Search/v3/Manufacturers");
        return response.payload?.Manufacturers ?? [];
    }
    async getRequestConfig() {
        const auth = await this.authenticator.authenticate();
        return {
            headers: {
                "X-DIGIKEY-Client-Id": auth.params.clientId,
                Authorization: `Bearer ${auth.token.accessToken}`,
            },
        };
    }
    updateRateLimit(headers) {
        if ("x-ratelimit-limit" in headers) {
            this.rateLimit = parseInt(String(headers["x-ratelimit-limit"] ?? ""));
        }
        if ("x-ratelimit-remaining" in headers) {
            this.rateLimitRemaining = parseInt(String(headers["x-ratelimit-remaining"] ?? ""));
        }
    }
}
function processSearchPayload(payload) {
    if ("ErrorMessage" in payload) {
        throw new Error(`Error ${payload.StatusCode}: ${payload.ErrorMessage}: ${payload.ErrorDetails}`);
    }
    return payload.ExactManufacturerProducts.map((part) => {
        const leadTimeDays = parseInt(part.ManufacturerLeadWeeks) * 7;
        return {
            distributor: "digikey",
            mpn: part.ManufacturerPartNumber,
            dpn: part.DigiKeyPartNumber,
            availableQuantity: part.QuantityAvailable,
            minimumOrderQuantity: part.MinimumOrderQuantity,
            datasheetUrl: part.PrimaryDatasheet,
            description: part.ProductDescription,
            leadTimeDays: _.isNaN(leadTimeDays) ? null : leadTimeDays,
            lifecycleStatus: part.ProductStatus,
            manufacturerName: part.Manufacturer?.Value,
            packagingOptions: _.compact([part.Packaging?.Value]),
            priceBreaks: part.StandardPricing.map((pb) => ({
                quantity: pb.BreakQuantity,
                price: dinero({ amount: Math.round(pb.UnitPrice * 100000), currency: USD, scale: 5 }),
                currency: payload.SearchLocaleUsed.Currency,
            })),
            listingUrl: part.ProductUrl,
            distributorAttributes: {
                distributor: "digikey",
                RoHSStatus: part.RoHSStatus,
                LeadStatus: part.LeadStatus,
                PrimaryPhoto: part.PrimaryPhoto,
                PrimaryVideo: part.PrimaryVideo,
                Series: part.Series,
                ManufacturerPageUrl: part.ManufacturerPageUrl,
                AlternatePackaging: part.AlternatePackaging,
                DetailedDescription: part.DetailedDescription,
                ReachStatus: part.ReachStatus,
                ExportControlClassNumber: part.ExportControlClassNumber,
                HTSUSCode: part.HTSUSCode,
                TariffDescription: part.TariffDescription,
                MoistureSensitivityLevel: part.MoistureSensitivityLevel,
                Family: part.Family,
                Category: part.Category,
                NonStock: part.NonStock,
                Packaging: part.Packaging,
                UnitPrice: part.UnitPrice,
                Manufacturer: part.Manufacturer,
                ManufacturerPublicQuantity: part.ManufacturerPublicQuantity,
                QuantityOnOrder: part.QuantityOnOrder,
                MaxQuantityForDistribution: part.MaxQuantityForDistribution,
                BackOrderNotAllowed: part.BackOrderNotAllowed,
                DKPlusRestriction: part.DKPlusRestriction,
                Marketplace: part.Marketplace,
                SupplierDirectShip: part.SupplierDirectShip,
                PimProductName: part.PimProductName,
                Supplier: part.Supplier,
                SupplierId: part.SupplierId,
                IsNcnr: part.IsNcnr,
                Parameters: _.reduce(part.Parameters, (acc, param) => {
                    acc[param.Parameter] = param.Value;
                    return acc;
                }, {}),
            },
        };
    });
}
const baseDigikeyLimitedTaxonomySchema = z.object({
    ProductCount: z.number(),
    NewProductCount: z.number(),
    ParameterId: z.number(),
    ValueId: z.string(),
    Parameter: z.string(),
    Value: z.string(),
});
const DigikeyLimitedTaxonomySchema = z.object({
    ...baseDigikeyLimitedTaxonomySchema.shape,
    Children: z.lazy(() => z.array(DigikeyLimitedTaxonomySchema)),
});
const DigikeyErrorResponseSchema = z.object({
    ErrorResponseVersion: z.string(),
    StatusCode: z.number(),
    ErrorMessage: z.string(),
    ErrorDetails: z.string(),
    RequestId: z.string(),
    ValidationErrors: z.unknown().array(),
});
const DigikeyExactSearchResponseSchema = z.object({
    SearchLocaleUsed: z.lazy(() => DigikeySearchLocaleSchema),
    ExactManufacturerProducts: z.lazy(() => z.array(DigikeyProductSchema)),
    ExactManufacturerProductsCount: z.number(),
});
const ExactSearchResponseOrErrorResponseSchema = z.union([
    DigikeyExactSearchResponseSchema,
    DigikeyErrorResponseSchema,
]);
const baseDigikeyProductSchema = z.object({
    RoHSStatus: z.string(),
    LeadStatus: z.string(),
    ProductUrl: z.string(),
    PrimaryDatasheet: z.string(),
    PrimaryPhoto: z.string(),
    PrimaryVideo: z.string(),
    ManufacturerLeadWeeks: z.string(),
    ManufacturerPageUrl: z.string(),
    ProductStatus: z.string(),
    DetailedDescription: z.string(),
    ReachStatus: z.string(),
    ExportControlClassNumber: z.string(),
    HTSUSCode: z.string(),
    TariffDescription: z.string(),
    MoistureSensitivityLevel: z.string(),
    ManufacturerPartNumber: z.string(),
    MinimumOrderQuantity: z.number(),
    NonStock: z.boolean(),
    QuantityAvailable: z.number(),
    DigiKeyPartNumber: z.string(),
    ProductDescription: z.string(),
    UnitPrice: z.number(),
    ManufacturerPublicQuantity: z.number(),
    QuantityOnOrder: z.number(),
    MaxQuantityForDistribution: z.number(),
    BackOrderNotAllowed: z.boolean(),
    DKPlusRestriction: z.boolean(),
    Marketplace: z.boolean(),
    SupplierDirectShip: z.boolean(),
    PimProductName: z.string(),
    Supplier: z.string(),
    SupplierId: z.number(),
    IsNcnr: z.boolean(),
    StandardPricing: z.lazy(() => z.array(DigikeyPriceBreakSchema)),
    Parameters: z.lazy(() => z.array(DigikeyProductParameterSchema)),
    Series: z.lazy(() => DigikeyProductParameterSchema),
    Family: z.lazy(() => DigikeyProductParameterSchema),
    Category: z.lazy(() => DigikeyProductParameterSchema),
    Packaging: z.lazy(() => DigikeyProductParameterSchema),
    Manufacturer: z.lazy(() => DigikeyProductParameterSchema),
});
const DigikeyProductSchema = z.object({
    ...baseDigikeyProductSchema.shape,
    AlternatePackaging: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
});
const DigikeyProductDetailsSchema = z.object({
    ...baseDigikeyProductSchema.shape,
    AlternatePackaging: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    MyPricing: z.lazy(() => z.array(DigikeyPriceBreakSchema)),
    Obsolete: z.boolean(),
    MediaLinks: z.lazy(() => z.array(DigikeyMediaLinkSchema)),
    StandardPackage: z.number(),
    LimitedTaxonomy: z.lazy(() => DigikeyLimitedTaxonomySchema),
    Kits: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    KitContents: z.lazy(() => z.array(DigikeyKitAssociationSchema)),
    MatingProducts: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    SearchLocaleUsed: z.lazy(() => DigikeySearchLocaleSchema),
    AssociatedProducts: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    ForUseWithProducts: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    RohsSubs: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    SuggestedSubs: z.lazy(() => z.array(DigikeyProductSuggestionSchema)),
    AdditionalValueFee: z.number(),
    ReachEffectiveDate: z.string(),
    ShippingInfo: z.string(),
});
const DigikeyProductSuggestionSchema = baseDigikeyProductSchema.pick({
    DigiKeyPartNumber: true,
    ManufacturerPartNumber: true,
    MinimumOrderQuantity: true,
    NonStock: true,
    Packaging: true,
    QuantityAvailable: true,
    ProductDescription: true,
    UnitPrice: true,
    Manufacturer: true,
    ManufacturerPublicQuantity: true,
    QuantityOnOrder: true,
    MaxQuantityForDistribution: true,
    BackOrderNotAllowed: true,
    DKPlusRestriction: true,
    Marketplace: true,
    SupplierDirectShip: true,
    PimProductName: true,
    Supplier: true,
    SupplierId: true,
    IsNcnr: true,
});
const DigikeyPriceBreakSchema = z.object({
    BreakQuantity: z.number(),
    UnitPrice: z.number(),
    TotalPrice: z.number(),
});
const DigikeyProductParameterSchema = z.object({
    ParameterId: z.number(),
    ValueId: z.string(),
    Parameter: z.string(),
    Value: z.string(),
});
const DigikeySearchLocaleSchema = z.object({
    Site: z.string(),
    Language: z.string(),
    Currency: z.string(),
    ShipToCountry: z.string().optional(),
});
const DigikeyMediaLinkSchema = z.object({
    MediaType: z.string(),
    Title: z.string(),
    SmallPhoto: z.string().nullable(),
    Thumbnail: z.string().nullable(),
    Url: z.string(),
});
const DigikeyKitAssociationSchema = z.object({
    AssociatedProduct: z.lazy(() => DigikeyProductSuggestionSchema),
    KitPartQuantity: z.number(),
});
const DigikeyManufacturerRecordSchema = z.object({
    Id: z.number(),
    Name: z.string(),
});
// QUESTION: what was this commented out for?
// const DigikeyFilterOptionValueSchema = z.object({
//   ValueId: z.string(),
//   Value: z.string(),
// });
//
// const DigikeyFilterOptionSchema = z.object({
//   Values: z.array(DigikeyFilterOptionValueSchema),
//   ParameterId: z.number(),
//   Parameter: z.string(),
// });
// QUESTION: what was this commented out for?
// const DigikeySearchResponseSchema = z.object({
//   LimitedTaxonomy: DigikeyLimitedTaxonomySchema.optional(),
//   FilterOptions: z.array(DigikeyFilterOptionSchema),
//   Products: z.lazy(() => z.array(DigikeyProductSchema)),
//   ProductsCount: z.number(),
//   ExactManufacturerProductsCount: z.number(),
//   ExactManufacturerProducts: z.lazy(() => z.array(DigikeyProductSchema)),
//   ExactDigiKeyProduct: z.lazy(() => z.nullable(DigikeyProductSchema)),
//   SearchLocaleUsed: z.lazy(() => DigikeySearchLocaleSchema.optional()),
// });
//
// type DigikeySearchResponse = z.infer<typeof DigikeySearchResponseSchema>;
//# sourceMappingURL=connector.js.map